import { Link } from 'react-router-dom';
import { MarketingShell } from '../components/MarketingShell';
import { IconMic, IconCamera, IconCheck, IconLock } from '../components/Icons';

// Landing page for mydyslexiatools.com. Audience: parents searching for help for their
// dyslexic child. Tone: emotional, calm, "this removes the barrier so your child can do
// this themselves." The 2x2 quadrant grid mirrors the agenticselling.io poster pattern
// but in the Ocean Blue palette.
export function MarketingScreen() {
  return (
    <MarketingShell>
      <Hero />
      <IntroPlusIllustration />
      <WowStatement />
      <HowItWorks />
      <FullWidthEmotion />
      <Quadrants />
      <BuiltByDyslexiaParent />
      <Features />
      <PrivacyReassurance />
      <StoreCallout />
      <FaqCta />
    </MarketingShell>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Hero (full width, headline only)
// ────────────────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="max-w-6xl mx-auto px-6 pt-16 pb-10 text-center">
      <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-accent font-medium mb-5">
        For kids and adults with dyslexia
      </p>
      <h1
        className="text-ink font-bold tracking-tight leading-[1.02] mx-auto max-w-4xl"
        style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 800 }}
      >
        The hardest part isn't knowing the answer...
        <br />
        <span className="text-accent">It is getting the answer onto the page.</span>
      </h1>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Intro paragraph + worksheet illustration
// ────────────────────────────────────────────────────────────────────────────

function IntroPlusIllustration() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-12">
      <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
        <div>
          <p className="text-lg sm:text-xl text-ink/75 leading-relaxed">
            My Dyslexia Tools helps children and adults complete paper worksheets more
            independently by removing the barriers around reading, writing, and filling in
            forms.
          </p>
          <p className="mt-5 text-base text-ink/60 leading-relaxed">
            No more handwriting that has to be deciphered the next day. No more juggling a
            printed worksheet next to answers typed on a laptop. No more sitting through every
            line with an adult beside you.
          </p>
        </div>

        <HeroIllustration />
      </div>
    </section>
  );
}

function HeroIllustration() {
  return (
    <div className="relative">
      <div className="absolute -top-3 -left-2 z-10 bg-white rounded-full shadow-lg shadow-ink/10 border border-ink/5 px-3 py-1.5 flex items-center gap-1.5 text-xs text-ink/70">
        <span className="text-accent">
          <IconCamera size={14} />
        </span>
        <span>Photographed from homework sheet</span>
      </div>

      <div
        className="bg-cream rounded-2xl border border-ink/10 shadow-2xl shadow-ink/10 p-7 transform -rotate-1"
        style={{
          backgroundImage:
            'repeating-linear-gradient(transparent, transparent 23px, rgba(26,26,46,0.04) 23px, rgba(26,26,46,0.04) 24px)',
        }}
      >
        {/* Header */}
        <div className="border-b border-ink/15 pb-3 mb-4">
          <p className="text-[10px] uppercase tracking-widest text-ink/40">Year 4 - English</p>
          <h3 className="text-base font-medium text-ink mt-1">Reading comprehension</h3>
        </div>

        {/* Passage */}
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-wider text-ink/40 mb-1.5">Read the passage</p>
          <p className="text-[13px] text-ink/75 leading-[24px]">
            The fox crept slowly through the long grass. He had been watching the chickens for
            hours. When the farmer finally turned away, he saw his chance.
          </p>
        </div>

        {/* Q1 - answered */}
        <ComprehensionQuestion
          n="1"
          q="What was the fox doing in the grass?"
          lines={[{ text: 'He was watching the chickens.', filled: true }, { text: '', filled: false }]}
        />

        {/* Q2 - current, being dictated */}
        <ComprehensionQuestion
          n="2"
          q="Why did the fox wait for the farmer to turn away?"
          current
          lines={[
            { text: 'Because he did not want to', filled: true },
            { text: 'be seen by the farmer.', filled: true },
            { text: '', filled: false },
          ]}
        />

        {/* Footer */}
        <div className="mt-5 pt-3 border-t border-ink/10 flex items-center justify-between text-[11px] text-ink/40">
          <span>1 of 2 answered</span>
          <span className="inline-flex items-center gap-1">
            <IconCheck size={10} /> Saving as PDF
          </span>
        </div>
      </div>

      <div className="absolute -bottom-6 right-2 lg:right-6 bg-white rounded-2xl border border-ink/10 shadow-xl shadow-ink/10 px-3 py-2.5 flex items-center gap-2.5 max-w-[240px]">
        <span className="w-9 h-9 rounded-full bg-accent text-white grid place-items-center flex-shrink-0 relative">
          <IconMic size={16} />
          <span className="absolute inset-0 rounded-full border-2 border-accent/40 animate-ping" />
        </span>
        <span className="text-[11px] text-ink/70 leading-tight">
          <span className="block font-medium text-ink">Reading aloud</span>
          <span className="block text-ink/50 italic">"Why did the fox wait..."</span>
        </span>
      </div>
    </div>
  );
}

function ComprehensionQuestion({
  n,
  q,
  lines,
  current = false,
}: {
  n: string;
  q: string;
  lines: { text: string; filled: boolean }[];
  current?: boolean;
}) {
  return (
    <div className={`relative ${current ? 'pl-2' : ''}`}>
      {current && (
        <span aria-hidden className="absolute -inset-x-3 -inset-y-1.5 rounded-xl bg-accentSoft/60 ring-2 ring-accent/30" />
      )}
      <div className="relative">
        <p className="flex items-start gap-2 text-[13px] text-ink/85 leading-[20px]">
          <span className="text-ink/50 font-medium">{n}.</span>
          <span>{q}</span>
        </p>
        <div className="mt-2 space-y-2.5">
          {lines.map((line, i) => (
            <div key={i} className="relative pb-1 border-b border-dashed border-ink/25">
              {line.filled && line.text ? (
                <span
                  className="text-accent text-[15px] font-medium inline-block transform -rotate-[1deg]"
                  style={{ fontFamily: '"Caveat", "Lexend Deca", cursive' }}
                >
                  {line.text}
                </span>
              ) : (
                <span aria-hidden className="text-transparent">_</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// WOW statement (new section above How It Works)
// ────────────────────────────────────────────────────────────────────────────

function WowStatement() {
  return (
    <section className="bg-cream border-y border-ink/5">
      <div className="max-w-5xl mx-auto px-6 py-20 sm:py-24 text-center">
        <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-accent font-medium">
          What it does
        </p>
        <h2
          className="mt-5 text-ink font-bold tracking-tight leading-[1.02] mx-auto max-w-4xl"
          style={{ fontSize: 'clamp(2.25rem, 5.5vw, 4rem)', fontWeight: 800 }}
        >
          Write or dictate straight <span className="text-accent">ON TO</span> your worksheet.
        </h2>
        <p className="mt-8 text-lg sm:text-xl text-ink/70 leading-relaxed max-w-3xl mx-auto">
          Take a photo of any worksheet. Hear each question read aloud. Speak or type your
          answer. Watch it appear in the right place on the page. Save the finished sheet as a
          PDF and send it back to school in minutes.
        </p>
        <p className="mt-5 italic text-base text-ink/55">
          Designed to support independence, not replace learning.
        </p>

        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link
            to="/app"
            className="inline-flex items-center justify-center bg-accent text-white px-7 py-4 rounded-full font-medium text-lg hover:brightness-105 transition shadow-lg shadow-accent/20"
          >
            Try it on your phone →
          </Link>
          <a
            href="#how-it-works"
            className="inline-flex items-center justify-center bg-white border border-ink/10 text-ink px-7 py-4 rounded-full font-medium text-lg hover:bg-warm transition"
          >
            See how it works →
          </a>
        </div>
        <p className="mt-4 text-xs text-ink/50">
          Works in your browser today. iPhone and Android apps coming soon.
        </p>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// How it works
// ────────────────────────────────────────────────────────────────────────────

function HowItWorks() {
  return (
    <section id="how-it-works" className="bg-white border-y border-ink/5">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-accent font-medium">
            How it works
          </p>
          <h2 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-ink leading-[1.05]">
            Three simple steps.
          </h2>
          <p className="mt-5 text-lg text-ink/65 leading-relaxed">
            No complicated setup. No learning curve. Simple enough for children to use
            independently.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-3">
          <Step
            n="1"
            title="Take a photo"
            body="Lay the worksheet flat on a table and open My Dyslexia Tools. The app is designed specifically for worksheets and school papers, guiding the camera towards the document clearly and simply."
          />
          <Step
            n="2"
            title="Answer each question"
            body="The app reads questions aloud one at a time using a calm, clear voice. Your child can speak the answer or type it. The answer is placed automatically into the correct location on the worksheet. No copying. No struggling to line things up. No losing track."
          />
          <Step
            n="3"
            title="Save and send"
            body="Export the finished worksheet as a PDF or image. Email it to school, print it, or keep it stored to come back to later."
          />
        </div>
      </div>
    </section>
  );
}

function Step({ n, title, body }: { n: string; title: string; body: string }) {
  return (
    <div className="bg-cream rounded-2xl p-7">
      <div className="w-12 h-12 rounded-full bg-accent text-white grid place-items-center font-bold text-xl">
        {n}
      </div>
      <h3 className="mt-5 font-semibold text-xl text-ink">{title}</h3>
      <p className="mt-3 text-ink/70 leading-relaxed text-[15px]">{body}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Full-width emotional centrepiece (Ocean Blue)
// ────────────────────────────────────────────────────────────────────────────

function FullWidthEmotion() {
  return (
    <section className="bg-accent text-white relative overflow-hidden">
      <div aria-hidden className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/5" />
      <div aria-hidden className="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-white/[0.03]" />

      <div className="relative max-w-4xl mx-auto px-6 py-24 sm:py-32 text-center">
        <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-white/60 font-medium">
          The real barrier
        </p>
        <h2 className="mt-5 text-4xl sm:text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight">
          Homework should not end <br className="hidden sm:block" />
          in frustration.
        </h2>

        <div className="mt-10 space-y-5 text-lg sm:text-xl leading-relaxed text-white/85 max-w-3xl mx-auto">
          <p>For many children with dyslexia, the hardest part isn't the work itself...</p>
          <p>
            It's trying to read the worksheet, hold the question in memory, process the words,
            organise the answer, and physically get it onto the page before confidence
            disappears.
          </p>
          <p className="text-white font-medium">My Dyslexia Tools removes that barrier.</p>
          <p>
            Children hear the question clearly, answer in their own way, and watch the
            worksheet fill itself in. Independently. Calmly. Without needing someone beside
            them for every line.
          </p>
        </div>

        <div className="mt-12 max-w-2xl mx-auto">
          <p className="text-sm uppercase tracking-[0.2em] text-white/50">
            Because confidence changes everything
          </p>
          <p
            className="mt-3 text-2xl sm:text-3xl italic font-medium leading-snug"
            style={{ fontFamily: '"Caveat", "Lexend Deca", cursive' }}
          >
            "I can actually do this on my own."
          </p>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// 2x2 emotion grid
// ────────────────────────────────────────────────────────────────────────────

function Quadrants() {
  // Mobile stacking: alternate light / dark / light / dark so we never get two
  // dark blocks in a row. Desktop keeps the natural 2x2 grid order (light/dark
  // on top row, dark/light on bottom). The bottom two are swapped on mobile only
  // via Tailwind order utilities (md+ reverts to DOM order).
  return (
    <section className="grid grid-cols-1 md:grid-cols-2">
      <Quadrant
        className="order-1 md:order-1"
        tone="light"
        eyebrow="On their own"
        title="WITHOUT A SCRIBE"
        lead="Finally able to complete worksheets independently."
        body="Without needing an adult beside them to read every question. Without trying to physically write when writing is the hardest part. Without falling behind because the answer was in their head all along. Children move through the work themselves, at their own pace."
        ctaLabel="See how it works"
        ctaHref="#how-it-works"
      />
      <Quadrant
        className="order-2 md:order-2"
        tone="dark"
        eyebrow="Less overwhelm"
        title="ONE AT A TIME"
        lead="Worksheets stop feeling like a wall of text."
        body="Any worksheet, of any size, can feel overwhelming before a child even begins. My Dyslexia Tools reduces the visual load by reading questions individually and filling answers directly into the correct place on the page. Less stress. More focus. More willingness to continue."
        ctaLabel="Try it on your phone"
        ctaHref="/app"
      />
      <Quadrant
        className="order-4 md:order-3"
        tone="dark"
        eyebrow="More confidence"
        title="ANSWERS COME EASIER"
        lead="The barrier disappears."
        body="Many dyslexic children already know the answer. The struggle is around reading, remembering, writing, and organising thoughts fast enough before confidence drops. Removing that friction shifts the focus back to learning, not battling the worksheet itself."
        ctaLabel="Watch it in action"
        ctaHref="/app"
      />
      <Quadrant
        className="order-3 md:order-4"
        tone="light"
        eyebrow="Less stress at home"
        title="CALMER EVENINGS"
        lead="Homework becomes calmer for the whole family."
        body="Parents should not need to become full-time readers, scribes, or translators every evening just to help homework get finished. My Dyslexia Tools helps children work more independently while reducing stress and homework battles at home."
        ctaLabel="Open the app"
        ctaHref="/app"
      />
    </section>
  );
}

interface QuadrantProps {
  tone: 'light' | 'dark';
  eyebrow: string;
  title: string;
  lead: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  className?: string;
}

function Quadrant({ tone, eyebrow, title, lead, body, ctaLabel, ctaHref, className = '' }: QuadrantProps) {
  const isDark = tone === 'dark';
  const bg = isDark ? 'bg-accent' : 'bg-cream';
  const eyebrowColor = isDark ? 'text-white/55' : 'text-ink/45';
  const titleColor = isDark ? 'text-white' : 'text-accent';
  const leadColor = isDark ? 'text-white' : 'text-ink';
  const bodyColor = isDark ? 'text-white/80' : 'text-ink/70';
  const ctaColor = isDark ? 'text-white hover:text-white/80' : 'text-accent hover:text-accentDark';
  const isInternal = ctaHref.startsWith('/');

  return (
    <article className={`${bg} ${className} px-7 sm:px-12 py-16 sm:py-20 min-h-[28rem] flex flex-col justify-between`}>
      <div>
        <p className={`text-[11px] sm:text-xs tracking-[0.25em] uppercase font-medium ${eyebrowColor}`}>
          {eyebrow}
        </p>
        <h3
          className={`mt-3 ${titleColor} leading-[0.9] tracking-tight`}
          style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 900, letterSpacing: '-0.02em' }}
        >
          {title}
        </h3>
        <p className={`mt-7 text-lg sm:text-xl font-medium leading-snug ${leadColor}`}>{lead}</p>
        <p className={`mt-4 text-[15px] leading-relaxed ${bodyColor} max-w-md`}>{body}</p>
      </div>

      <div className="mt-8">
        {isInternal ? (
          <Link to={ctaHref} className={`inline-flex items-center font-medium text-base ${ctaColor} transition`}>
            {ctaLabel} <span aria-hidden className="ml-1">→</span>
          </Link>
        ) : (
          <a href={ctaHref} className={`inline-flex items-center font-medium text-base ${ctaColor} transition`}>
            {ctaLabel} <span aria-hidden className="ml-1">→</span>
          </a>
        )}
      </div>
    </article>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Built By A Dyslexia Parent (elaborated with the son's story)
// ────────────────────────────────────────────────────────────────────────────

function BuiltByDyslexiaParent() {
  return (
    <section id="why" className="bg-white border-y border-ink/5">
      <div className="max-w-5xl mx-auto px-6 py-24">
        <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-accent font-medium">
          Why this exists
        </p>
        <h2 className="mt-3 text-3xl sm:text-4xl xl:text-5xl font-bold tracking-tight text-ink leading-[1.05]">
          Built by a dyslexia parent.
          <br />
          <span className="text-accent">For families who have been there.</span>
        </h2>

        <div className="mt-10 grid gap-10 lg:grid-cols-2 lg:items-start">
          <div className="space-y-5 text-lg leading-relaxed text-ink/75">
            <p>
              My son is in the <strong className="text-ink font-semibold">1st centile for writing</strong>. He knows the answers. He struggles to get them onto the page.
            </p>
            <p>
              Every week he comes home with worksheet after worksheet to complete. His real-world choices today are both broken:
            </p>
            <ul className="space-y-3 pl-1">
              <li className="flex gap-3">
                <span className="text-accent font-bold leading-7">·</span>
                <span>
                  Try to write the answers in his own hand. Then sit down later and try to{' '}
                  <strong className="text-ink font-semibold">decipher</strong> what he wrote, because the handwriting that took him forever to produce is now unusable for revision.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="text-accent font-bold leading-7">·</span>
                <span>
                  Type his answers on a laptop. Then try to line up "Answer to question 4" on a screen with "Question 4" on a piece of paper sitting beside him. Far from ideal, and a real barrier to actually learning what the worksheet is teaching.
                </span>
              </li>
            </ul>
            <p className="text-accent font-medium text-xl sm:text-2xl leading-snug pt-2">
              These are not tools. They are workarounds. They are the gap between knowing the answer and getting it onto the page that dyslexic children fall into every single day.
            </p>
          </div>

          <div className="space-y-5 text-lg leading-relaxed text-ink/75 lg:pl-6 lg:border-l lg:border-ink/10">
            <p className="text-ink font-medium">My Dyslexia Tools is the tool I wished existed.</p>
            <p>
              Simple enough for children to use confidently. Fast enough for real homework. Respectful enough that nobody feels singled out. The worksheet stays the worksheet. The answers go where they belong.
            </p>
            <p>
              And critically, the finished sheet is something a child can look back at later, send to a teacher, or use for revision, because the answers are in the right place on the page they were written on.
            </p>

            <div className="mt-10 pt-8 border-t border-ink/10">
              <p className="text-xs uppercase tracking-[0.25em] text-accent font-medium">My Dyslexia Tools</p>
              <p
                className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-ink leading-tight"
                style={{ fontWeight: 800 }}
              >
                Lifting the barrier to learning. <br />
                <span className="text-accent">One tool at a time.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Features
// ────────────────────────────────────────────────────────────────────────────

function Features() {
  return (
    <section className="bg-cream/40">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-accent font-medium">Features</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-ink leading-tight">
            Designed for the way dyslexic minds work.
          </h2>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <Feature
            title="Question read aloud"
            body="Built-in text-to-speech reads each question clearly using a calm voice designed to reduce pressure and improve focus. Dyslexia-friendly fonts and slower reading speed options are built throughout the experience."
          />
          <Feature
            title="Voice or typing"
            body="Speak the answer naturally or type it manually. Both work. Both place answers directly into the correct area on the worksheet. There is no wrong way to use the app."
          />
          <Feature
            title="Secure accounts and saved work"
            body="Worksheets are stored so they can be reopened later from any device. Parents can keep schoolwork organised without losing papers, screenshots, or photos between home and school. Simple login. Minimal setup."
          />
          <Feature
            title="Built for privacy and safety"
            body="The app is designed specifically around worksheets and educational use. Photos stay private and secure. We never sell personal information or use worksheets for advertising. Privacy-first by design."
          />
        </div>
      </div>
    </section>
  );
}

function Feature({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-white rounded-2xl border border-ink/10 p-7">
      <h3 className="font-semibold text-lg text-ink">{title}</h3>
      <p className="mt-3 text-ink/70 leading-relaxed text-[15px]">{body}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Privacy reassurance
// ────────────────────────────────────────────────────────────────────────────

function PrivacyReassurance() {
  return (
    <section className="bg-warm/40 border-y border-ink/5">
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-accent font-medium inline-flex items-center gap-1.5">
          <IconLock size={12} /> Privacy first
        </p>
        <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-ink leading-tight">
          Built for children. Designed for trust.
        </h2>
        <div className="mt-7 space-y-4 text-lg leading-relaxed text-ink/75">
          <p>Photos remain secure throughout the process.</p>
          <p>
            AI-assisted worksheet detection is on by default because it is what makes the
            recognition accurate. Photos are processed and removed immediately afterwards. You
            can turn AI mode off any time from Settings, and the first-run notice makes clear
            how to do it.
          </p>
          <p>
            The same safeguards apply either way: no body images, no faces, documents only.
            No ads. No tracking across the web. No unnecessary data collection.
          </p>
        </div>
        <Link to="/privacy" className="inline-flex items-center mt-8 text-accent font-medium hover:underline">
          Read the full privacy policy →
        </Link>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Get the apps
// ────────────────────────────────────────────────────────────────────────────

function StoreCallout() {
  return (
    <section className="max-w-5xl mx-auto px-6 py-20 text-center">
      <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-accent font-medium">
        Get the apps
      </p>
      <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-ink leading-tight">
        Available in your browser today.
      </h2>
      <p className="mt-4 text-ink/60 text-lg">iPhone and Android apps are currently in review.</p>
      <div className="mt-9 flex flex-wrap justify-center gap-4">
        <StorePlaceholder label="App Store" sub="Coming soon" />
        <StorePlaceholder label="Google Play" sub="Coming soon" />
        <Link
          to="/app"
          className="inline-flex flex-col items-center justify-center bg-accent text-white px-5 py-3 rounded-2xl min-w-[200px] hover:brightness-105 transition shadow-lg shadow-accent/20"
        >
          <span className="text-xs uppercase tracking-wider opacity-80">Available now</span>
          <span className="text-base font-semibold mt-0.5">Open in your browser</span>
        </Link>
      </div>
    </section>
  );
}

function StorePlaceholder({ label, sub }: { label: string; sub: string }) {
  return (
    <div className="bg-white border border-ink/10 rounded-2xl px-5 py-3 min-w-[200px] text-left opacity-60">
      <p className="text-xs uppercase tracking-wider text-ink/50">{sub}</p>
      <p className="text-base font-semibold mt-0.5">{label}</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Questions CTA
// ────────────────────────────────────────────────────────────────────────────

function FaqCta() {
  return (
    <section className="border-t border-ink/5">
      <div className="max-w-3xl mx-auto px-6 py-20 text-center">
        <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-accent font-medium">
          Questions?
        </p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-ink leading-tight">
          We answer every email personally.
        </h2>
        <p className="mt-4 text-ink/65 text-lg">
          Tell us how the app could better support your child, classroom, or family.
        </p>
        <Link
          to="/support"
          className="inline-flex items-center mt-8 bg-white border border-ink/10 text-ink px-6 py-3.5 rounded-full font-medium hover:bg-warm transition"
        >
          Get in touch →
        </Link>
      </div>
    </section>
  );
}
